/**
 * GoogleFormModal Component
 * 
 * A reusable modal component for displaying Google Form embeds.
 * Matches the existing website theme and Modal component styling.
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {function} props.onClose - Close callback function
 * @param {string|JSX} props.embedCode - Google Form iframe embed code (optional)
 * @param {string} props.title - Modal title (optional, default: "Join C2 Community")
 * @param {string} props.maxWidth - Maximum width (optional, default: "700px")
 */

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './GoogleFormModal.css';

const DEFAULT_EMBED_CODE = (
    <iframe
        src="https://docs.google.com/forms/d/e/1FAIpQLSd38WkM68YNcpABLYA47b__MVHKaw6GKE05t2_Ba1uxtIqtsA/viewform?embedded=true"
        width="100%"
        height="1762"
        frameBorder="0"
        marginHeight="0"
        marginWidth="0"
        title="C2 Community Membership Form"
    >
        Loading…
    </iframe>
);

const GoogleFormModal = ({
    isOpen,
    onClose,
    embedCode = DEFAULT_EMBED_CODE,
    title = "Join C2 Community",
    maxWidth = "700px"
}) => {
    const modalRef = useRef(null);
    const previousActiveElement = useRef(null);

    useEffect(() => {
        if (isOpen) {
            // Store currently focused element
            previousActiveElement.current = document.activeElement;

            // Lock body scroll
            document.body.style.overflow = 'hidden';

            // Focus modal
            if (modalRef.current) {
                modalRef.current.focus();
            }
        } else {
            // Restore body scroll
            document.body.style.overflow = '';

            // Return focus to previous element
            if (previousActiveElement.current) {
                previousActiveElement.current.focus();
            }
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    const modalContent = (
        <div
            className={`google-form-modal-overlay ${isOpen ? 'google-form-modal-overlay--open' : ''}`}
            onClick={handleOverlayClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby="google-form-modal-title"
        >
            <div
                className="google-form-modal-container"
                style={{ maxWidth }}
                ref={modalRef}
                tabIndex={-1}
            >
                <div className="google-form-modal-header">
                    <h2 id="google-form-modal-title" className="google-form-modal-title">
                        {title}
                    </h2>
                    <button
                        onClick={onClose}
                        className="google-form-modal-close"
                        aria-label="Close modal"
                    >
                        ×
                    </button>
                </div>
                <div className="google-form-modal-body">
                    {typeof embedCode === 'string' ? (
                        <div dangerouslySetInnerHTML={{ __html: embedCode }} />
                    ) : (
                        embedCode
                    )}
                </div>
            </div>
        </div>
    );

    const modalRoot = document.getElementById('modal-root');
    return modalRoot ? createPortal(modalContent, modalRoot) : null;
};

export default GoogleFormModal;
